import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { Star, MessageSquare, QrCode, Shield, Bell, TrendingUp, Globe, AlertTriangle, BarChart3, MapPin, Cpu } from "lucide-react";

const features = [
  {
    icon: MessageSquare,
    title: "Automated Review Requests",
    description: "Timed SMS and email requests sent automatically after each job. Your customers leave reviews without you lifting a finger.",
  },
  {
    icon: QrCode,
    title: "Manual & QR Code Review Options",
    description: "Physical and digital QR codes your team can hand to happy customers on site for instant review capture — plus manual send options.",
  },
  {
    icon: Bell,
    title: "Google Review Reactivation Campaign",
    description: "Targeted SMS and email campaigns to past customers who never left a review — re-engaging them to share their experience and boost your review count fast.",
  },
  {
    icon: Star,
    title: "Professional Review Responses",
    description: "We craft professional responses to all reviews — both positive and negative — so your brand always looks engaged and trustworthy.",
  },
  {
    icon: Shield,
    title: "Negative Review Protection",
    description: "Unhappy customers are routed to private feedback first, giving you a chance to resolve issues before they go public.",
  },
  {
    icon: AlertTriangle,
    title: "Fake & Spam Review Disputes",
    description: "We identify and flag fake or spam reviews on your behalf through Google's official channels to help protect your rating.",
  },
  {
    icon: TrendingUp,
    title: "Social Proof Syndication",
    description: "Your best reviews are automatically shared to Facebook and Instagram, turning 5-star feedback into social proof across platforms.",
  },
  {
    icon: Globe,
    title: "Monthly Reporting & Insights",
    description: "Monthly reports on review growth, ratings trends, and actionable insights to keep your reputation on an upward trajectory.",
  },
  {
    icon: BarChart3,
    title: "Review Growth Strategy",
    description: "Monthly review targets, performance tracking, and optimisation to keep your review count growing consistently.",
  },
];

const benefits = [
  "Triple your Google reviews within 2 months",
  "Build trust before customers even call you",
  "Higher Google Maps rankings from more reviews",
  "Automated system — no manual follow-ups needed",
  "Protect your reputation from negative reviews",
  "Stand out against competitors with fewer reviews",
  "Increased quote acceptance rates",
  "Social proof that sells for you 24/7",
];

export default function Reputation() {
  return (
    <ServicePageTemplate
      title="Reputation Management & Review Generation"
      subtitle="Automated 5-star review collection that builds trust and social proof — on autopilot."
      metaTitle="Reputation Management & Reviews for Tradies | FullScale Marketing"
      metaDescription="Automated Google review collection for Australian tradies. Triple your reviews in 2 months with our reputation management system."
      canonicalUrl="https://fullscalemarketing.com.au/services/reputation"
      badgeText="Reputation & Reviews"
      icon={Star}
      introText="Customers trust online reviews as much as personal recommendations. If your competitors have more reviews and a higher rating, they're getting calls that should be yours. Our automated review system makes it effortless to collect 5-star Google reviews consistently — and when combined with local SEO and automation tools, your online presence becomes unstoppable."
      features={features}
      benefits={benefits}
      ctaHeading="Ready to Build an Unbeatable Online Reputation?"
      ctaText="Book a free strategy call and we'll show you how to triple your Google reviews and build trust that wins more jobs."
      relatedServices={[
        {
          icon: MapPin,
          title: "Google Business Profile & SEO",
          description: "More reviews directly boost your Google Maps ranking and local search visibility.",
          href: "/services/gbp-seo",
        },
        {
          icon: Cpu,
          title: "Automation & AI Tools",
          description: "Automate review requests, follow-ups, and responses with our AI-powered tools.",
          href: "/services/automation",
        },
        {
          icon: Globe,
          title: "SMART Websites",
          description: "Showcase your reviews on a professional website that converts visitors into leads.",
          href: "/services/websites",
        },
      ]}
      faqs={[
        {
          question: "How quickly will I start getting more reviews?",
          answer: "Most businesses see a noticeable increase within the first 2 weeks. Our automated system sends review requests at the optimal time after each job, so results start flowing almost immediately.",
        },
        {
          question: "What happens if I get a negative review?",
          answer: "Our negative review protection routes unhappy customers to private feedback first, giving you a chance to resolve the issue. If a negative review does go live, we craft a professional response and can dispute fake or spam reviews on your behalf.",
        },
        {
          question: "Do I need to do anything manually?",
          answer: "Nope. Once set up, the system runs on autopilot. Review requests are sent automatically via SMS and email after each job. You can also use QR codes on-site for an extra boost.",
        },
        {
          question: "Will more reviews actually help me get more jobs?",
          answer: "Absolutely. More 5-star reviews increase your Google Maps ranking, build instant trust with potential customers, and directly increase your quote acceptance rate. Reviews are one of the highest-ROI marketing assets you can build.",
        },
      ]}
    />
  );
}
